import React, { useEffect, useState, useCallback } from 'react';
import {
  getRecruitmentAnalytics,
  getRecruitmentTrends,
  getRecommendations,
  getPredictiveAnalytics,
  exportRecruitmentReport,
  getStageDuration,
  getComparisonFunnel,
} from '../../api/recruitmentAnalytics';
import MetricsCards from './components/MetricsCards';
import FunnelChart from './components/FunnelChart';
import SourceEffectiveness from './components/SourceEffectiveness';
import TimeToHireChart from './components/TimeToHireChart';
import {
  AlertCircle, CheckCircle, Download, TrendingUp, Sparkles,
  Clock, User, ArrowRight, BarChart2, Calendar, Bell, Building2
} from 'lucide-react';
import Loader from '../../components/Loader';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = [
  'rgba(106, 17, 203, 0.7)',
  'rgba(37, 117, 252, 0.7)',
  'rgba(40, 167, 69, 0.7)',
  'rgba(255, 77, 79, 0.7)',
  'rgba(250, 173, 20, 0.7)',
  'rgba(155, 89, 182, 0.7)',
];

const RecruitmentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [trends, setTrends] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [predictive, setPredictive] = useState<any>(null);
  const [exporting, setExporting] = useState(false);
  const [staleVacancies, setStaleVacancies] = useState<any[]>([]);
  const [topCandidates, setTopCandidates] = useState<any[]>([]);
  const [stageDuration, setStageDuration] = useState<any[]>([]);
  const [comparisonFunnel, setComparisonFunnel] = useState<any>(null);
  const [alertsCount, setAlertsCount] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [analyticsRes, trendsRes, recsRes, predRes, stageDurRes, compFunnelRes] = await Promise.all([
        getRecruitmentAnalytics(),
        getRecruitmentTrends({ days: 90 }),
        getRecommendations(),
        getPredictiveAnalytics().catch(() => null),
        getStageDuration(),
        getComparisonFunnel(),
      ]);
      
      // Преобразуем строковые значения в числа
      if (analyticsRes) {
        const processed = { ...analyticsRes };
        (processed.timeMetrics as any).avgTimeToFill = parseFloat(String(analyticsRes.timeMetrics.avgTimeToFill)) || 0;
        (processed.timeMetrics as any).avgTimeToHire = parseFloat(String(analyticsRes.timeMetrics.avgTimeToHire)) || 0;
        (processed.summary as any).overallConversionRate = parseFloat(String(analyticsRes.summary.overallConversionRate)) || 0;
        (processed.costMetrics as any).avgCostPerHire = parseFloat(String(analyticsRes.costMetrics.avgCostPerHire)) || 0;
        (processed.qualityMetrics as any).avgPerformanceScore = parseFloat(String(analyticsRes.qualityMetrics.avgPerformanceScore)) || 0;
        processed.sourceEffectiveness = processed.sourceEffectiveness?.map((s: any) => ({
          ...s,
          conversionRate: parseFloat(String(s.conversionRate)) || 0,
        }));
        setAnalytics(processed);
      } else {
        setAnalytics(analyticsRes);
      }
      
      setTrends(trendsRes);
      setRecommendations(recsRes.recommendations);
      if (predRes) setPredictive(predRes);
      setStageDuration(stageDurRes);
      setComparisonFunnel(compFunnelRes);

      try {
        const alertsRes = await api.get('/recruitment-analytics/alerts');
        setAlertsCount(alertsRes.data.length);
      } catch (e) {}

      const [vacRes, candRes] = await Promise.all([
        api.get('/vacancies'),
        api.get('/candidates'),
      ]);

      // Долго открытые вакансии (только реально открытые, исключая терминальные статусы)
      const terminalStatuses = ['cancelled', 'placement', 'filled', 'закрыта', 'closed'];
      const now = new Date();
      const stale = vacRes.data
        .filter((v: any) => {
          const status = (v.status || '').toLowerCase();
          if (terminalStatuses.includes(status)) return false;
          const created = new Date(v.createdAt);
          const daysOpen = Math.ceil((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
          return daysOpen > 30;
        })
        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setStaleVacancies(stale.slice(0, 5));

      // Топ‑5 кандидатов в работе (расширенные этапы)
      const finalStageTitles = ['интервью', 'собеседование', 'оффер', 'тестовое', 'interview', 'offer'];
      const finalStageIds = new Set<string>();
      try {
        const stagesRes = await api.get('/candidate-stages');
        stagesRes.data.forEach((s: any) => {
          if (s.title && finalStageTitles.some(t => s.title.toLowerCase().includes(t))) {
            finalStageIds.add(s._id);
          }
        });
      } catch (e) {}
      const top = candRes.data
        .filter((c: any) => finalStageIds.has(c.stage) || c.status === 'interview' || c.status === 'offer')
        .sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 5);
      setTopCandidates(top);
    } catch (err) {
      console.error('Ошибка загрузки:', err);
      setError('Не удалось загрузить данные.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3600000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportRecruitmentReport('excel');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recruitment-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('Отчёт выгружен', 'success');
    } catch (err) {
      showToast('Ошибка выгрузки отчёта', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleMatchCandidates = () => {
    navigate('/app/compatibility?returnUrl=/app/recruitment-analytics');
  };

  const handleAlerts = () => {
    navigate('/app/recruitment-analytics/alerts');
  };

  const handleDigest = () => {
    navigate('/app/recruitment-analytics/daily-digest');
  };

  const handleGoToCRM = () => {
    navigate('/app/hr');
  };

  if (loading) return <Loader />;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: 'red' }}>{error}</div>;
  if (!analytics) return null;

  const { summary, funnel, timeMetrics, costMetrics, sourceEffectiveness, qualityMetrics } = analytics;

  const funnelData = Array.isArray(funnel) ? funnel : [
    { stage: 'Заявки', value: funnel.applications },
    { stage: 'Скрининг', value: funnel.screenings || funnel.applications * 0.7 },
    { stage: 'Интервью', value: funnel.interviews || funnel.applications * 0.4 },
    { stage: 'Офферы', value: funnel.offers || funnel.applications * 0.15 },
    { stage: 'Наймы', value: funnel.hires },
  ];

  const comparisonFunnelData = comparisonFunnel
    ? ['applications', 'screenings', 'interviews', 'offers', 'hires'].map(key => ({
        stage: key === 'applications' ? 'Заявки' : key === 'screenings' ? 'Скрининг' : key === 'interviews' ? 'Интервью' : key === 'offers' ? 'Офферы' : 'Наймы',
        current: comparisonFunnel.current[key] || 0,
        previous: comparisonFunnel.previous[key] || 0,
      }))
    : [];

  return (
    <div style={{ padding: '0 20px 20px 20px', position: 'relative', zIndex: 1 }}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            Аналитика рекрутинга
          </h1>
          <p style={styles.subtitle}>Сегодня: {new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div style={styles.actions}>
          <button onClick={handleGoToCRM} style={styles.crmButton}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #2575fc, #6a11cb)'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'transparent'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = '#2575fc'; e.currentTarget.style.borderColor = '#2575fc'; }}>
            <Building2 size={16} /><span>ЛАМА HRM</span>
          </button>
          <button onClick={handleMatchCandidates} style={styles.matchButton}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #5a0fb0, #1a5fde)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,117,252,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #6a11cb, #2575fc)'; e.currentTarget.style.boxShadow = 'none'; }}>
            <Sparkles size={16} /> Мэтчинг кандидатов
          </button>
          <button onClick={handleDigest} style={styles.digestButton}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #2ecc71, #27ae60)'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'transparent'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = '#2ecc71'; e.currentTarget.style.borderColor = '#2ecc71'; }}>
            <Calendar size={16} /><span>Дайджест</span>
          </button>
          <button onClick={handleAlerts} style={styles.alertsButton}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #f39c12, #e67e22)'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'transparent'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = '#f39c12'; e.currentTarget.style.borderColor = '#f39c12'; }}>
            <Bell size={16} />
            {alertsCount > 0 && <span style={styles.badge}>{alertsCount}</span>}
            <span>Алерты</span>
          </button>
          <button onClick={handleExport} disabled={exporting} style={styles.exportButton}
            onMouseEnter={(e) => { if (!exporting) { e.currentTarget.style.background = 'linear-gradient(135deg, #2575fc, #1a5fde)'; e.currentTarget.style.color = 'white'; e.currentTarget.style.borderColor = 'transparent'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = '#2575fc'; e.currentTarget.style.borderColor = '#2575fc'; }}>
            <Download size={16} /> {exporting ? 'Выгрузка...' : 'Выгрузить отчёт'}
          </button>
        </div>
      </div>

      <MetricsCards
        totalVacancies={summary.totalVacancies}
        totalCandidates={summary.totalCandidates}
        totalHires={summary.totalHires}
        avgTimeToFill={timeMetrics.avgTimeToFill}
        avgCostPerHire={costMetrics.avgCostPerHire}
        conversionRate={summary.overallConversionRate}
      />

      {predictive && (
        <div style={styles.predictiveCard}>
          <h3><TrendingUp size={18} /> Предиктивная аналитика</h3>
          <div style={styles.predictiveContent}>
            <span>Прогноз закрытия вакансий: <strong>{predictive.predictedTimeToFill} дней</strong></span>
            <span>Ожидаемые наймы в след. месяце: <strong>{predictive.predictedHiresNextMonth}</strong></span>
            <div>
              Риски оттока:
              {predictive.attritionRisk.map((r: any) => (
                <span key={r.department} style={{ marginLeft: 8, color: r.risk > 30 ? '#e74c3c' : '#f1c40f' }}>
                  {r.department}: {r.risk}%
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={styles.chartGrid}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Воронка подбора</h3>
          <FunnelChart data={funnelData} />
        </div>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Эффективность источников</h3>
          <SourceEffectiveness data={sourceEffectiveness} colors={COLORS} />
        </div>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Динамика по дням (90 дней)</h3>
          <TimeToHireChart data={trends} />
        </div>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Качество найма</h3>
          <div style={{ padding: 20 }}>
            <div style={styles.qualityItem}>
              <span>Удержание 90 дней</span>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${qualityMetrics.retentionRate90d}%` }} />
                <span>{qualityMetrics.retentionRate90d}%</span>
              </div>
            </div>
            <div style={styles.qualityItem}>
              <span>Удержание 180 дней</span>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${qualityMetrics.retentionRate180d}%` }} />
                <span>{qualityMetrics.retentionRate180d}%</span>
              </div>
            </div>
            <div style={styles.qualityItem}>
              <span>Средняя оценка</span>
              <span style={{ fontWeight: 600 }}>{qualityMetrics.avgPerformanceScore}/5</span>
            </div>
          </div>
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}><Calendar size={16} /> Среднее время на этапах (дни)</h3>
          {stageDuration.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stageDuration} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="avgDays" fill="rgba(106, 17, 203, 0.8)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p style={{ color: '#999' }}>Недостаточно данных</p>}
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}><BarChart2 size={16} /> Воронка: текущий vs прошлый месяц</h3>
          {comparisonFunnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={comparisonFunnelData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="current" fill="rgba(37, 117, 252, 0.7)" name="Текущий месяц" radius={[8, 8, 0, 0]} />
                <Bar dataKey="previous" fill="rgba(106, 17, 203, 0.4)" name="Прошлый месяц" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p style={{ color: '#999' }}>Нет данных</p>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 30 }}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}><Clock size={16} /> Долго открытые вакансии</h3>
          {staleVacancies.length === 0 ? (
            <p style={{ color: '#999' }}>Все вакансии в пределах нормы</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {staleVacancies.map((vac: any) => (
                <li key={vac.id || vac._id} style={{ padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.05)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                  onClick={() => navigate(`/app/recruitment-analytics/vacancies/${vac.id || vac._id}`)}>
                  <span>{vac.title}</span>
                  <span style={{ color: '#e67e22' }}>{Math.ceil((new Date().getTime() - new Date(vac.createdAt).getTime()) / (1000 * 60 * 60 * 24))} дн.</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}><User size={16} /> Топ‑5 кандидатов в работе</h3>
          {topCandidates.length === 0 ? (
            <p style={{ color: '#999' }}>Нет кандидатов на финальных этапах</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {topCandidates.map((c: any) => (
                <li key={c._id} style={{ padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.05)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                  onClick={() => navigate(`/app/hr/candidates/${c._id}`)}>
                  <span>{c.firstName} {c.lastName}</span>
                  <span style={{ color: '#2575fc', fontSize: 12 }}>{c.position || c.email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Блок рекомендаций */}
      {recommendations.length > 0 && (
        <div style={styles.recommendationsCard}>
          <h3 style={styles.chartTitle}>Рекомендации</h3>
          <div style={styles.recommendationsList}>
            {recommendations.map((rec, idx) => (
              <div key={idx} style={{ ...styles.recommendationItem, borderLeftColor: rec.type === 'critical' ? '#e74c3c' : rec.type === 'warning' ? '#f1c40f' : '#2575fc' }}>
                {rec.type === 'critical' ? <AlertCircle size={18} color="#e74c3c" /> : rec.type === 'warning' ? <AlertCircle size={18} color="#f1c40f" /> : <CheckCircle size={18} color="#2575fc" />}
                <div>
                  <strong>{rec.title}</strong>
                  <p>{rec.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, flexWrap: 'wrap', gap: 16 },
  title: { fontSize: 32, fontWeight: 800, color: '#1a1a2e', marginBottom: 5, letterSpacing: '-0.02em' },
  subtitle: { fontSize: 16, color: '#64748b' },
  actions: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  crmButton: {
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(37,117,252,0.4)',
    color: '#2575fc',
    borderRadius: 30,
    padding: '10px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(37,117,252,0.1)',
  },
  matchButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: 30,
    padding: '10px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    transition: 'all 0.2s',
    boxShadow: '0 8px 20px rgba(37,117,252,0.3)',
  },
  digestButton: {
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(46,204,113,0.4)',
    color: '#2ecc71',
    borderRadius: 30,
    padding: '10px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(46,204,113,0.1)',
  },
  alertsButton: {
    position: 'relative',
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(243,156,18,0.4)',
    color: '#f39c12',
    borderRadius: 30,
    padding: '10px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(243,156,18,0.1)',
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    background: '#e74c3c',
    color: 'white',
    borderRadius: '50%',
    width: 20,
    height: 20,
    fontSize: 11,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #fff',
  },
  exportButton: {
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(37,117,252,0.4)',
    color: '#2575fc',
    borderRadius: 30,
    padding: '10px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(37,117,252,0.1)',
  },
  predictiveCard: {
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 24,
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 12px 40px rgba(31,38,135,0.08)',
    border: '1px solid rgba(255,255,255,0.6)',
  },
  predictiveContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    fontSize: 15,
    color: '#1a1a2e',
  },
  chartGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: '24px',
    marginBottom: '24px',
  },
  chartCard: {
    background: 'rgba(255,255,255,0.55)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 24,
    padding: '24px',
    boxShadow: '0 12px 40px rgba(31,38,135,0.06)',
    border: '1px solid rgba(255,255,255,0.6)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: 700,
    margin: '0 0 20px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#1a1a2e',
  },
  qualityItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid rgba(0,0,0,0.04)',
  },
  progressBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    maxWidth: '300px',
  },
  progressFill: {
    height: '8px',
    borderRadius: '4px',
    background: 'linear-gradient(90deg, #6a11cb, #2575fc)',
    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 6px rgba(106,17,203,0.3)',
  },
  recommendationsCard: {
    background: 'rgba(255,255,255,0.55)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 24,
    padding: '24px',
    boxShadow: '0 12px 40px rgba(31,38,135,0.06)',
    border: '1px solid rgba(255,255,255,0.6)',
    marginBottom: '24px',
  },
  recommendationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  recommendationItem: {
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start',
    padding: '16px',
    borderLeft: '4px solid #2575fc',
    background: 'rgba(255,255,255,0.5)',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
  },
};

export default RecruitmentDashboard;
